<template>
  <section>
    <b-overlay :show="loading">
      <validation-observer
        ref="refFormObserver"
        #default="{ handleSubmit, invalid }"
      >
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <!-- Col: Left (albaran Container) -->
            <b-col
              cols="12"
              lg="9"
              xl="9"
              md="9"
            >
              <b-card no-body>
                <!-- Header -->
                <b-card-body class="pb-0">
                  <h3 class="text-primary mb-2">
                    Codigo #{{ albaranData.codigo }}
                  </h3>
                  <div
                    class="d-flex justify-content-between flex-md-row flex-column mt-0"
                  >
                    <!-- Header: Left Content -->
                    <div>
                      <b-card-text class="mb-1">
                        <strong> Creado por: </strong>
                        {{
                          albaranData.fk_user_creado || user ? user.name : '-'
                        }}
                      </b-card-text>
                      <b-card-text
                        v-if="!isLibreMode"
                        class="mb-1"
                      >
                        <strong> Departamento:</strong>
                        {{ albaranData.pedido_departamento_titulo }}
                      </b-card-text>
                      <b-card-text
                        v-else
                        class="mb-1"
                      >
                        <strong>Albarán de entrega directa</strong>
                      </b-card-text>
                    </div>

                    <!-- Header: Right Content -->
                    <div class="mt-md-0 mt-2">
                      <b-row
                        align-h="end"
                        class="pr-1 mb-2"
                      >
                        <b-badge :variant="statusVariant(albaranData.estado)">{{
                          statusLabel(albaranData.estado)
                        }}
                        </b-badge>
                      </b-row>
                      <div class="d-flex align-items-center mb-1">
                        <span class="title pr-1"> Fecha: </span>
                        <validation-provider
                          #default="validationContext"
                          name="Fecha"
                          rules="required"
                        >
                          <b-form-group
                            :state="getValidationState(validationContext)"
                          >
                            <flat-pickr
                              v-model="albaranData.fecha"
                              class="form-control"
                              :config="{
                                format: 'd-m-Y',
                                altFormat: 'd-m-Y',
                                allowInput: true,
                                altInput: true,
                                locale: Spanish,
                              }"
                              :disabled="albaranData.estado === 'entregado'"
                            />
                            <b-form-invalid-feedback
                              :state="getValidationState(validationContext)"
                            >
                              {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>
                    </div>
                  </div>
                </b-card-body>

                <!-- Entregado a -->
                <b-card-body>
                  <label>Entregado a</label>
                  <v-select
                    v-model="albaranData.fk_empleado_recoge"
                    placeholder="Selecione"
                    :options="empleados"
                    :clearable="true"
                    label="empleadoInfo"
                    :reduce="(val) => val.id"
                    :disabled="albaranData.estado === 'entregado'"
                  />
                </b-card-body>

                <!-- Note -->
                <b-card-body class="albaran-padding pt-0">
                  <span class="font-weight-bold">Nota: </span>
                  <b-form-textarea
                    v-model="albaranData.nota"
                    :disabled="albaranData.estado === 'entregado'"
                  />
                </b-card-body>

                <!-- Spacer -->
                <hr>

                <h3 class="pl-2 mb-1">
                  Detalles de la entrega
                </h3>

                <!-- Selector de material (solo modo libre y nuevo albarán) -->
                <b-card-body
                  v-if="isLibreMode && $route.params.id === 'new'"
                  class="pt-0"
                >
                  <label>Añadir material (buscar por nombre o escanear código de barras)</label>
                  <v-select
                    v-model="selectedMaterial"
                    :options="uniformeMateriales"
                    label="displayName"
                    placeholder="Buscar por nombre, código o barcode..."
                    :clearable="true"
                    :filter-by="filterMaterial"
                    @input="handleMaterialSelect"
                  >
                    <template #option="option">
                      {{ option.displayName }}
                    </template>
                    <template #selected-option="option">
                      {{ option.displayName }}
                    </template>
                  </v-select>
                </b-card-body>

                <!-- Tabla modo libre -->
                <b-table
                  v-if="isLibreMode"
                  :items="albaranItems"
                  responsive
                  striped
                  :fields="tableHeadersLibre"
                >
                  <template #cell(refProd)="data">
                    {{ data.item.refProd }}
                  </template>
                  <template #cell(descripcion)="data">
                    <router-link
                      target="_blank"
                      :to="{ name: 'material-edit', params: { id: data.item.fk_material } }"
                    >
                      <slot>{{ data.item.descripcion_custom || data.item.descripcion }}
                        <feather-icon icon="ExternalLinkIcon" size="16" class="align-top ml-0" />
                      </slot>
                    </router-link>
                  </template>
                  <template #cell(unit_entregadas)="data">
                    <b-form-input
                      v-model="data.item.unit_entregadas"
                      type="number"
                      :disabled="albaranData.estado === 'entregado'"
                      style="width: 80px"
                      :class="{ 'border-warning': Number(data.item.unit_entregadas) < 0 }"
                    />
                    <small
                      v-if="Number(data.item.unit_entregadas) < 0"
                      style="color: darkorange"
                    >Devolución</small>
                    <small
                      v-else
                      style="color: red"
                    >{{ checkStockLibre(data.item) }}</small>
                  </template>
                  <template
                    v-if="$route.params.id === 'new'"
                    #cell(actions)="data"
                  >
                    <b-button
                      variant="danger"
                      size="sm"
                      class="btn-icon"
                      @click="removeItem(data.index)"
                    >
                      <feather-icon icon="TrashIcon" />
                    </b-button>
                  </template>
                </b-table>

                <!-- Tabla modo con pedido (comportamiento original) -->
                <b-table
                  v-else
                  :items="albaranItems"
                  responsive
                  striped
                  :fields="tableHeaders"
                >
                  <!-- Ref Prod -->
                  <template #cell(refProd)="data">
                    {{ data.item.refProd }}
                  </template>
                  <!-- Descripcion -->
                  <template #cell(descripcion)="data">
                    <router-link
                      target="_blank"
                      :to="{ name: 'material-edit', params: { id: data.item.fk_material } }"
                    >
                      <slot>{{ data.item.descripcion_custom || data.item.descripcion }}
                        <feather-icon
                          icon="ExternalLinkIcon"
                          size="16"
                          class="align-top ml-0"
                        />
                      </slot>
                    </router-link>
                  </template>

                  <!-- Cantidad Solicitada -->
                  <template #cell(unit_solicitadas)="data">
                    {{ data.item.unit_solicitadas }}
                  </template>

                  <!-- Stock (real / reservado / pendiente) -->
                  <template #cell(stock_info)="data">
                    <small>stock real: <strong>{{ data.item.stock_real || 0 }}</strong></small><br>
                    <small>stock reservado: <strong>{{ data.item.stock_reservado || 0 }}</strong></small><br>
                    <small>stock pendiente: <strong>{{ data.item.stock_pendiente || 0 }}</strong></small>
                  </template>

                  <!-- Cantidad Entregada -->
                  <template #cell(unit_entregadas)="data">
                    <b-form-group
                      v-if="data.item.delivered_on"
                    >
                      <span>
                        {{ data.item.unit_entregadas }} entregadas el {{ formatDate(data.item.delivered_on) }}
                      </span>
                    </b-form-group>
                    <validation-provider
                      v-else
                      #default="validationContext"
                      name="Cantidad Entregada"
                    >
                      <b-form-group
                        :state="getValidationState(validationContext)"
                      >
                        <b-form-input
                          v-model="data.item.unit_entregadas"
                          type="number"
                          :disabled="data.item.unit_entregadas === data.item.unit_solicitadas"
                        />
                        <b-form-invalid-feedback
                          :state="getValidationState(validationContext)"
                        >
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                        <small
                          v-if="data.item.delivered_part && data.item.ya_entregadas > 0"
                          style="color: blue; display: block"
                        >Ya se han entregado {{ data.item.ya_entregadas }} de {{ data.item.unit_solicitadas }}
                        </small>
                        <small
                          v-if="!data.item.delivered_on"
                          style="color: red; display: block"
                        >{{ checkStock(data.item) }}
                        </small>
                      </b-form-group>
                    </validation-provider>
                  </template>
                </b-table>
              </b-card>
            </b-col>

            <!-- Right Col: Card -->
            <b-col
              cols="12"
              lg="3"
              md="3"
              xl="3"
              class="albaran-actions mt-md-0 mt-2"
            >
              <!-- Action Buttons -->
              <b-card>
                <!-- Button: Save albaran -->
                <b-button
                  v-if="albaranData.estado !== 'entregado'"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mb-75"
                  block
                  type="submit"
                  :disabled="invalid"
                >
                  <feather-icon
                    icon="SaveIcon"
                    size="16"
                  />
                  Guardar
                </b-button>

                <!-- Button: Send albaran -->
                <b-button
                  v-if="$route.params.id !== 'new'"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-primary"
                  class="mb-75"
                  block
                  :disabled="invalid"
                  @click="printAlbaran(albaranData.id)"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                  />
                  Imprimir
                </b-button>

                <b-button
                  v-if="$route.params.id !== 'new' && !isLibreMode"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="warning"
                  class="mb-75"
                  block
                  @click="goToPedido(albaranData.fk_pedido)"
                >
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="16"
                  />
                  Ir a pedido
                </b-button>

                <!-- Button: Delete -->
                <b-button
                  v-if="canDeleteAlbaran"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="danger"
                  block
                  @click="deleteModal = true"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                  />
                  Eliminar
                </b-button>

                <!-- Spacer -->
                <div v-if="albaranData.estado !== 'entregado' && !isLibreMode">
                  <hr>
                  <label>Cambiar estado</label>
                  <v-select
                    v-model="albaranData.estado"
                    :options="albaranes.estados"
                    :reduce="(est) => est.value"
                    :clearable="false"
                  />
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>

    <ConfirmationModal
      :id="`confirmation-${$route.params.id}`"
      :show="deleteModal"
      message="Esta acción no se puede revertir y se perderá el albaran."
      title="¿Está seguro de que desea eliminar el albaran?"
      @action="deleteAlbaran($route.params.id)"
      @close="deleteModal = false"
    />

    <AlertModal
      :id="`alert-${$route.params.id}`"
      :show="alertModal"
      :message="alertMsg"
      :title="alertTitle"
      variant="danger"
      :is-ok-only="true"
      @close="alertModal = false"
      @action="alertModal = false"
    />

  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BFormTextarea,
  BFormCheckbox,
  BPopover,
  VBToggle,
  BBadge,
  BTable,
  BTooltip,
  BFormInvalidFeedback,
  BOverlay,
  BLink,
} from 'bootstrap-vue'
import Logo from '@core/layouts/components/Logo.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import vSelect from 'vue-select'
import { Spanish } from 'flatpickr/dist/l10n/es'
import flatPickr from 'vue-flatpickr-component'
import albaranesStoreModule from '@/views/almacen/albaranes/albaranesStoreModule'
import Albaran from '@/models/Albaran'
import materialesStoreModule from '@/views/almacen/materiales/materialesStoreModule'
import { albaranes } from '@/utils/albaranes'
import router from '@/router'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import AlertModal from '@/components/AlertModal.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import formValidation from '@core/comp-functions/forms/form-validation'
import { required } from '@validations'
import moment from 'moment'
import useEmpleadosList from '@/views/system/empleado/useEmpleadosList'
import empleadoStoreModule from '@/views/system/empleado/empleadoStoreModule'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormTextarea,
    BFormCheckbox,
    BBadge,
    BPopover,
    BTable,
    BTooltip,
    BLink,
    flatPickr,
    vSelect,
    Logo,
    ConfirmationModal,
    AlertModal,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BOverlay,
    BFormInvalidFeedback,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  data() {
    return {
      deleteModal: false,
      alertModal: false,
      alertMsg: '',
      alertTitle: '',
      selectedItem: {},
      Spanish,
      totalEntregado: 0,
      albaranes,
      required,
      barcodeInput: '',
      selectedMaterial: null,
    }
  },
  computed: {
    user() {
      return this.$store.state.auth.user
    },
    canDeleteAlbaran() {
      return !!(this.albaranData.estado === 'pendiente'
          && this.albaranData.fk_empleado_recoge === null && this.noDeleteAlbaran)
    },
    loading() {
      return this.$store.state['app-albaranes'].loading
    },
    isLibreMode() {
      return this.isLibre
    },
  },
  methods: {
    checkStock(item) {
      const disponible = (item.stock_real || 0) + (item.stock_reservado || 0)
      const pendiente = (item.unit_solicitadas || 0) - (item.ya_entregadas || 0)
      return disponible < pendiente && pendiente > 0
        ? 'No hay suficientes materiales en stock para entregar todo el pedido!'
        : ''
    },
    checkStockLibre(item) {
      const qty = Number(item.unit_entregadas)
      if (qty <= 0) return ''
      return qty > Number(item.stock_real)
        ? `Stock insuficiente (disponible: ${item.stock_real})`
        : ''
    },
    removeItem(index) {
      this.albaranItems.splice(index, 1)
    },
    handleMaterialSelect(material) {
      if (!material) return
      const existingIndex = this.albaranItems.findIndex(item => item.fk_material === material.id)
      if (existingIndex >= 0) {
        this.albaranItems[existingIndex].unit_entregadas = Number(this.albaranItems[existingIndex].unit_entregadas) + 1
      } else {
        this.albaranItems.push({
          fk_material: material.id,
          refProd: material.codigo,
          descripcion: material.descripcion,
          descripcion_custom: material.descripcion_custom,
          stock_real: material.stock_real,
          unit_entregadas: 1,
          unit_solicitadas: 0,
          delivered_on: null,
          ya_entregadas: 0,
        })
      }
      this.$nextTick(() => { this.selectedMaterial = null })
    },
    printAlbaran(id) {
      this.$store
        .dispatch('app-albaranes/printAlbaran', id)
        .then(response => {
          window.open(response.route, '_blank')
        })
        .catch(error => {
          console.error('Error printing el albarán:', error)
        })
    },
    goToPedido(idPedido) {
      this.$router.push({
        name: 'pedido-edit',
        params: { id: idPedido },
      })
    },
    deleteAlbaran(id) {
      this.$store.dispatch('app-albaranes/deleteAlbaran', id)
        .then(() => {
          this.$router.push({ name: 'albaranes-list' })
        })
    },
    statusLabel(status) {
      return albaranes.estados.find(el => el.value === status)?.label
    },
    statusVariant(status) {
      return albaranes.estados.find(el => el.value === status)?.variant
    },
    onSubmit() {
      this.albaranData.fk_user_update = this.user.id
      if (!this.albaranData.fk_user_creado) {
        this.albaranData.fk_user_creado = this.user.id
      }
      this.albaranData.detalles = [...this.albaranItems]

      // fk_empleado_recoge ya es el id (el v-select usa :reduce), no requiere conversión.

      let invalidDelivery = false

      if (this.isLibreMode) {
        // Modo libre: validar stock solo en entregas (qty > 0); devoluciones (qty < 0) siempre OK
        this.albaranData.detalles.forEach(a => {
          const entregadas = Number(a.unit_entregadas)
          if (entregadas > 0 && entregadas > Number(a.stock_real)) {
            invalidDelivery = true
            this.alertModal = true
            this.alertTitle = 'Error de stock'
            this.alertMsg = `No hay suficiente stock para "${a.descripcion_custom || a.descripcion}". Disponible: ${a.stock_real}`
          }
        })
        if (!invalidDelivery) {
          this.albaranData.estado = 'entregado'
        }
      } else {
        // Modo con pedido: lógica original
        this.albaranData.detalles.forEach(a => {
          const entregadas = Number(a.unit_entregadas)
          const solicitadas = Number(a.unit_solicitadas)

          if (entregadas !== 0) {
            const disponible = Number(a.stock_real || 0) + Number(a.stock_reservado || 0)
            const pendienteEntregar = entregadas - Number(a.ya_entregadas)
            if (disponible < pendienteEntregar && pendienteEntregar > 0) invalidDelivery = true
            if (entregadas > solicitadas) invalidDelivery = true

            if (invalidDelivery) {
              this.alertModal = true
              this.alertMsg = 'No puede entregar unidades solicitadas de materiales que no tienen stock. Refresca la página o revisa el campo entrega de cada material.'
              this.alertTitle = 'Error'
            }
          }

          if (entregadas === solicitadas && !invalidDelivery) {
            if (a.delivered_on === null) {
              moment.locale('es')
              a.delivered_on = moment(new Date().toDateString())
                .format('YYYY-MM-DD HH:mm:ss')
            }
            this.totalEntregado += 1
          }
        })

        if (this.totalEntregado >= this.albaranData.detalles.length && !invalidDelivery) {
          this.albaranData.estado = 'entregado'
        }
      }

      if (this.albaranData.estado === 'entregado' && this.albaranData.fk_empleado_recoge && !invalidDelivery) {
        this.$store.dispatch('app-empleado/updateEmpleadoUniformStatus', {
          empleadoId: this.albaranData.fk_empleado_recoge,
          uniformStatus: true,
        })
      }
      if (!invalidDelivery) {
        this.$store
          .dispatch('app-albaranes/addAlbaran', this.albaranData)
          .then(() => {
            this.$router.push({ name: 'albaranes-list' })
          })
      }
    },
  },
  setup() {
    const MATERIALES_APP_STORE_MODULE_NAME = 'app-materiales'
    const ALBARANES_APP_STORE_MODULE_NAME = 'app-albaranes'
    const EMPLEADO_APP_STORE_MODULE_NAME = 'app-empleado'

    // Register module

    if (!store.hasModule(MATERIALES_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        MATERIALES_APP_STORE_MODULE_NAME,
        materialesStoreModule,
      )
    }

    if (!store.hasModule(ALBARANES_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        ALBARANES_APP_STORE_MODULE_NAME,
        albaranesStoreModule,
      )
    }

    if (!store.hasModule(EMPLEADO_APP_STORE_MODULE_NAME)) store.registerModule(EMPLEADO_APP_STORE_MODULE_NAME, empleadoStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MATERIALES_APP_STORE_MODULE_NAME)) store.unregisterModule(MATERIALES_APP_STORE_MODULE_NAME)

      if (store.hasModule(ALBARANES_APP_STORE_MODULE_NAME)) store.unregisterModule(ALBARANES_APP_STORE_MODULE_NAME)

      if (store.hasModule(EMPLEADO_APP_STORE_MODULE_NAME)) store.unregisterModule(EMPLEADO_APP_STORE_MODULE_NAME)
    })

    const noDeleteAlbaran = ref(false)

    const isLibre = ref(router.currentRoute.query.tipo === 'libre')

    const {
      fetchEmpleadosNoPaginate,
      searchQuery,
      totalEmpleados,
    } = useEmpleadosList()

    const empleados = ref([])

    const refetchEmpleados = async () => {
      try {
        const res = await fetchEmpleadosNoPaginate()

        const { total, data } = res
        totalEmpleados.value = total
        const filterActive = data.filter(el => el.estado === 1)
        empleados.value = filterActive.map(el => ({
          ...el,
          empleadoInfo: `#${el.matricula} - ${el.nombre} ${el.apellidos} - ${el.departamento_name}`,
        }))

        totalEmpleados.value = filterActive.length
      } catch (e) {
        console.error('Error fetching empleados:', e)
      }
    }

    refetchEmpleados()

    const albaranData = ref(new Albaran({}))

    const albaranItems = ref([])

    const uniformeMateriales = ref([])

    const updateItemForm = (index, val) => {
      const { unit_entregadas } = val
      albaranItems.value[index].unit_entregadas = unit_entregadas
    }

    const isNewAlbaran = router.currentRoute.params.id === 'new'

    if (isNewAlbaran) {
      // Inicializar valores por defecto para nuevo albarán
      albaranData.value.fecha = moment().format('YYYY-MM-DD')
      albaranData.value.estado = 'pendiente'
    } else {
      store
        .dispatch('app-albaranes/fetchAlbaran', router.currentRoute.params.id)
        .then(async response => {
          albaranData.value = response
          if (!response.fk_pedido) isLibre.value = true
          const { detalles } = albaranData.value
          // fk_empleado_recoge se deja como id: el v-select "Entregado a" usa
          // :reduce="val => val.id", por lo que el v-model debe ser el id (no el objeto).
          // Convertirlo a objeto rompía el select (incoherencia con :reduce + race
          // condition con la carga de empleados) y dejaba el campo vacío.

          for (let i = 0; i < detalles.length; i++) {
            const material = await store.dispatch(
              'app-materiales/fetchMaterial',
              detalles[i].fk_material,
            )

            const object = Object.assign(detalles[i], {
              refProd: material.codigo,
              talla: material.talla,
              stock_real: material.stock_real,
              stock_reservado: material.stock_reservado,
              stock_pendiente: material.stock_pendiente,
              tipo: material.tipo,
              desechable: material.desechable === 1,
            })

            if (detalles[i].unit_entregadas === 0) detalles[i].delivered_part = false

            if (detalles[i].unit_entregadas !== detalles[i].unit_solicitadas) {
              noDeleteAlbaran.value = true
              detalles[i].delivered_part = true
              detalles[i].ya_entregadas = detalles[i].unit_entregadas
            }

            if (detalles[i].delivered_on !== null) {
              noDeleteAlbaran.value = true
            }

            albaranItems.value.push(object)
          }
        })
    }

    // Cargar materiales tipo uniforme para el selector
    store.dispatch('app-materiales/listMateriales', {
      body: { search: '' },
      headers: { 'X-Sort': 'materiales.descripcion', 'X-Sort-Order': 'asc' },
      query: {},
    }).then(res => {
      const all = (res && res.data) ? res.data : []
      uniformeMateriales.value = all
        .filter(m => m.estado === 'activo')
        .map(m => ({
          ...m,
          displayName: `${m.codigo} - ${m.descripcion_custom || m.descripcion}`,
          // searchLabel incluye código, descripción y barcode para que vue-select los encuentre
          searchLabel: [m.codigo, m.descripcion, m.descripcion_custom, m.barcode]
            .filter(Boolean).join(' '),
        }))
    })

    const tableHeaders = ref([
      { key: 'refProd', label: 'REF PROD', sortable: false, thStyle: { width: '10%' } },
      { key: 'descripcion', label: 'Descripcion', sortable: false, thStyle: { width: '35%' } },
      { key: 'unit_solicitadas', label: 'Cant. Solicitada', sortable: false, thStyle: { width: '10%' } },
      { key: 'unit_entregadas', label: 'Cant. Entregada', sortable: false, thStyle: { width: '24%' } },
      { key: 'stock_info', label: 'Stock', sortable: false },
    ])

    const tableHeadersLibre = ref([
      { key: 'refProd', label: 'REF PROD', sortable: false },
      { key: 'descripcion', label: 'Descripción', sortable: false },
      { key: 'unit_entregadas', label: 'Cantidad', sortable: false },
      { key: 'actions', label: '', sortable: false },
    ])

    const resetAlbaranData = () => {
      albaranData.value = JSON.parse(JSON.stringify(new Albaran({})))
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetAlbaranData)

    return {
      albaranData,
      updateItemForm,
      albaranItems,
      tableHeaders,
      tableHeadersLibre,
      refFormObserver,
      getValidationState,
      resetForm,
      searchQuery,
      empleados,
      refetchEmpleados,
      noDeleteAlbaran,
      isLibre,
      uniformeMateriales,
      filterMaterial: (option, label, search) => {
        if (!search) return true
        const s = search.toLowerCase()
        const str = v => (v == null ? '' : String(v)).toLowerCase()
        return str(option.codigo).includes(s)
          || str(option.descripcion).includes(s)
          || str(option.descripcion_custom).includes(s)
          || str(option.barcode).includes(s)
      },
    }
  },
}
</script>

<style lang="scss">
    @import "@core/scss/vue/libs/vue-select.scss";
    @import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style scoped>
    .pagination {
        display: flex;
        margin: 0.25rem 0.25rem 0;
    }

    .pagination button {
        flex-grow: 1;
    }

    .pagination button:hover {
        cursor: pointer;
    }
</style>
